import { useMemo } from 'react';
import { RfUiInput, RfUiSelect } from 'renfe-components';
import {
  makeSelectIsStudentProfile,
  makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected,
} from 'store/usuarios/selectors';
import {
  makeSelectCursosPracticaParse,
  makeSelectGetActivatedJourneyCourses,
  makeSelectPracticaSeleccionada,
  makeSelectTiposPractica,
  makeSelectTiposServicio,
} from 'store/practicas/selectors';
import { language } from 'utils/literals';
import { stateLiteral } from 'utils/statesLiterals';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSelectedCourse,
  setIdTypePracticeSelected,
  setTypeServiceSelected,
  setVehicleSelected,
  setTurnSelected,
  setTrainSelected,
  setStartDateTimeSelectedPreparation,
  setStartTimeSelectedPreparationPristine,
  setTypePracticeSelected,
  setDisposicionPracticeSelected,
} from 'store/app/actions';
import {
  makeSelectCourseCreateSelected,
  makeSelectNumTurnoSelected,
  makeSelectSelectedJourney,
  makeSelectTrainSelected,
  makeSelectIdTypePracticeSelected,
  makeSelectTypeServiceSelected,
  makeSelectVehicleSelected,
  makeSelectStartDateTimeSelectedJourney,
  makeSelectTypePracticeSelected,
} from 'store/app/selectors';
import {
  CursoPractica,
  PracticaSeleccionada,
  TipoPractica,
  TipoServicio,
} from 'store/practicas/types';

const selectors = createStructuredSelector({
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  practicaSeleccionada: makeSelectPracticaSeleccionada(),
  activatedJourneyCourses: makeSelectGetActivatedJourneyCourses(),
  selectedJourney: makeSelectSelectedJourney(),
  optionCourses: makeSelectCursosPracticaParse(),
  selectedCourse: makeSelectCourseCreateSelected(),
  tiposPractica: makeSelectTiposPractica(),
  idTypePracticeSelected: makeSelectIdTypePracticeSelected(),
  typePracticeSelected: makeSelectTypePracticeSelected(),
  tiposServicio: makeSelectTiposServicio(),
  typeServiceSelected: makeSelectTypeServiceSelected(),
  selectedVehicle: makeSelectVehicleSelected(),
  selectedTrain: makeSelectTrainSelected(),
  selectedTurn: makeSelectNumTurnoSelected(),
  startDateTimeSelectedJourney: makeSelectStartDateTimeSelectedJourney(),
  isStudentProfile: makeSelectIsStudentProfile(),
} as any);

const PracticeInformation = (props: any) => {
  const dispatch = useDispatch();
  const {
    practicaSeleccionada,
    activatedJourneyCourses,
    selectedCourse,
    tiposPractica,
    idTypePracticeSelected,
    tiposServicio,
    typeServiceSelected,
    selectedVehicle,
    selectedTrain,
    selectedTurn,
    startDateTimeSelectedJourney,
    isStudentProfile,
  } = useSelector<any, any>(selectors);

  const editMode: boolean = useMemo(
    () => !!practicaSeleccionada.CUERPO_FICHA,
    [practicaSeleccionada]
  );

  const calculatedCourseOptions = useMemo(() => {
    const options = activatedJourneyCourses.map((e: CursoPractica) => ({
      label: `${e?.ID_CURSO} - ${e.NOMBRE_CURSO}`,
      value: e,
      active:
        selectedCourse.toString() === e?.ID_CURSO.toString() ? 'true' : 'false',
    }));
    return JSON.stringify(options);
  }, [activatedJourneyCourses, selectedCourse]);

  const calculatedIdTypePracticeSelectedOptions = useMemo(() => {
    const options = tiposPractica.map((option: TipoPractica) => ({
      label: option?.ID_TIPO_PRACTICA + ' - ' + option.DESC_PRACTICA,
      value: option,
      active:
        idTypePracticeSelected === option.ID_TIPO_PRACTICA.toString()
          ? 'true'
          : 'false',
    }));
    return JSON.stringify(options);
  }, [tiposPractica, idTypePracticeSelected]);

  const calculatedTypeServiceSelectedOptions = useMemo(() => {
    const options = tiposServicio.map((e: TipoServicio) => ({
      label: e?.ID_SERVICIO + ' - ' + e.DESC_SERVICIO,
      value: e,
      active: typeServiceSelected === e?.ID_SERVICIO ? 'true' : 'false',
    }));
    return JSON.stringify(options);
  }, [tiposServicio, typeServiceSelected]);

  const calculatedTrainIsDisabled: boolean = useMemo(() => {
    return idTypePracticeSelected === 'MA' ? true : false;
  }, [idTypePracticeSelected]);

  // Condiciones de edición

  const canEditSelectedCourse: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditIdTypePractice: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditTypeService: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditSelectedVehicle: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  const canEditSelectedTrain: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1 &&
      !calculatedTrainIsDisabled
    );
  }, [isStudentProfile, practicaSeleccionada, idTypePracticeSelected]);

  const canEditSelectedTurn: boolean = useMemo(() => {
    const estadoPractica =
      (practicaSeleccionada as PracticaSeleccionada)?.CUERPO_FICHA
        ?.ID_ESTADO_PRACTICA ?? null;
    return (
      isStudentProfile &&
      !!estadoPractica &&
      [stateLiteral.borrador, stateLiteral.pendCorrecion].indexOf(
        estadoPractica
      ) > -1
    );
  }, [isStudentProfile, practicaSeleccionada]);

  // Eventos

  const handleChangeSelectedCourse = (e: CustomEvent) => {
    const courseId = e?.detail?.value?.ID_CURSO ?? '';
    dispatch(setSelectedCourse(courseId));
  };

  const handleChangeIdTypePractice = (e: CustomEvent) => {
    const newTypePracticeSelected = e.detail?.value?.TIPO_PRACTICA ?? '';
    const newIdTypePracticeSelected = e.detail?.value?.ID_TIPO_PRACTICA ?? '';
    const newAvailabilityPracticeSelected =
      e.detail?.value?.PUESTA_DISPOSICION ?? '';
    dispatch(setIdTypePracticeSelected(newIdTypePracticeSelected));
    dispatch(setTypePracticeSelected(newTypePracticeSelected));
    dispatch(setDisposicionPracticeSelected(newAvailabilityPracticeSelected));

    // Si el tipo de práctica seleccionada está PUESTA EN DISPOSICIÓN, se habilita el campo.
    const enableChanged =
      newAvailabilityPracticeSelected === 'S' ? true : false;
    if (enableChanged) {
      const newStartDateTimeSelectedPreparation = startDateTimeSelectedJourney
        ? new Date(startDateTimeSelectedJourney)
        : new Date();
      newStartDateTimeSelectedPreparation.setHours(0, 0, 0, 0);
      dispatch(
        setStartDateTimeSelectedPreparation(newStartDateTimeSelectedPreparation)
      );
      dispatch(setStartTimeSelectedPreparationPristine(true));
    } else {
      dispatch(setStartDateTimeSelectedPreparation(null));
      dispatch(setStartTimeSelectedPreparationPristine(true));
    }
    // Si la práctica es tipo MA, se setea el tren. Si no, se limpia
    if (newIdTypePracticeSelected === 'MA') {
      dispatch(setTrainSelected('MBRA'));
    } else {
      dispatch(setTrainSelected(''));
    }
  };

  const handleChangeTypeServiceSelected = (e: CustomEvent) => {
    const id = e.detail?.value?.ID_SERVICIO ?? '';
    dispatch(setTypeServiceSelected(id));
  };

  const handleChangeSelectedVehicle = (e: CustomEvent) => {
    const data = e.detail ?? '';
    dispatch(setVehicleSelected(data));
  };

  const handleChangeSelectedTrain = (e: CustomEvent) => {
    const data = e.detail ?? '';
    dispatch(setTrainSelected(data));
  };
  const handleChangeSelectedTurn = (e: CustomEvent) => {
    const data = e.detail ?? '';
    dispatch(setTurnSelected(data));
  };

  return (
    <>
      {editMode ? (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.infoGeneralPracticaTitle}
          </h2>
          <div className="rf-practice-detail__input-container">
            <RfUiSelect
              required={'black'}
              textError={language.validations.selectEmpty}
              options={calculatedCourseOptions}
              disabled={!canEditSelectedCourse}
              label={language.informacionPractica.cursoLabel}
              onOptionActive={handleChangeSelectedCourse}
              placeHolder={
                language.informacionPractica.seleccionCursoPlaceholder
              }
            />
          </div>
          {!isStudentProfile && (
            <div className="rf-practice-detail__input-container">
              <RfUiInput
                label={language.informacionPractica.sociedadCursoLabel}
                error=""
                className="rf-input"
                disabled
                value={
                  practicaSeleccionada?.CABECERA_FICHA?.ID_SOCIEDAD_CURSO +
                  ' - ' +
                  practicaSeleccionada?.CABECERA_FICHA?.SOCIEDAD_CURSO
                }
              />
            </div>
          )}
          <div className="rf-practice-detail__input-container">
            <RfUiSelect
              textError={language.validations.selectEmpty}
              label={language.informacionPractica.tipoPracticaTitle}
              disabled={!canEditIdTypePractice}
              options={calculatedIdTypePracticeSelectedOptions}
              onOptionActive={handleChangeIdTypePractice}
              placeHolder={language.informacionPractica.tipoPracticaPlaceholder}
            />
          </div>
          <div className="rf-practice-detail__input-container">
            <RfUiSelect
              textError={language.validations.selectEmpty}
              disabled={!canEditTypeService}
              options={calculatedTypeServiceSelectedOptions}
              onOptionActive={handleChangeTypeServiceSelected}
              label={language.informacionPractica.tipoServicioLabel}
              placeHolder={language.informacionPractica.tipoServicioPlaceholder}
            />
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-detail__input-container rf-practice-create__input-container--pr">
              <RfUiInput
                label={language.informacionPractica.vehiculoLabel}
                disabled={!canEditSelectedVehicle}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedVehicle}
                value={selectedVehicle}
                placeholder={language.informacionPractica.vehiculoLabel}
                maxLenght="6"
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-detail__input-container">
              <RfUiInput
                label={language.informacionPractica.trenLabel}
                disabled={!canEditSelectedTrain}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedTrain}
                value={selectedTrain}
                placeholder={language.informacionPractica.trenLabel}
                maxLenght="6"
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-detail__input-container">
              <RfUiInput
                disabled={!canEditSelectedTurn}
                label={language.informacionPractica.turnoLabel}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedTurn}
                value={selectedTurn}
                placeholder={language.informacionPractica.turnoLabel}
                maxLenght="10"
              />
            </div>
          </div>
        </div>
      ) : (
        <div>
          <h2 className="rf-title rf-title--secundary">
            {language.informacionPractica.infoGeneralPracticaTitle}
          </h2>
          <div className="rf-practice-create__input-container">
            <RfUiSelect
              required={'black'}
              textError={language.validations.selectEmpty}
              options={calculatedCourseOptions}
              label={language.informacionPractica.cursoLabel}
              onOptionActive={handleChangeSelectedCourse}
              placeHolder={language.informacionPractica.cursoPlaceholder}
            />
          </div>
          <div className="rf-practice-create__input-container">
            <RfUiSelect
              textError={language.validations.selectEmpty}
              label={language.informacionPractica.tipoPracticaLabel}
              options={calculatedIdTypePracticeSelectedOptions}
              onOptionActive={handleChangeIdTypePractice}
              placeHolder={language.informacionPractica.tipoPracticaPlaceholder}
            />
          </div>
          <div className="rf-practice-create__input-container">
            <RfUiSelect
              textError={language.validations.selectEmpty}
              options={calculatedTypeServiceSelectedOptions}
              onOptionActive={handleChangeTypeServiceSelected}
              label={language.informacionPractica.tipoServicioLabel}
              placeHolder={language.informacionPractica.tipoServicioPlaceholder}
            />
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container rf-practice-create__input-container--pr">
              <RfUiInput
                label={language.informacionPractica.vehiculoLabel}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedVehicle}
                maxLenght="6"
                placeholder={language.informacionPractica.vehiculoLabel}
                value={selectedVehicle}
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container">
              <RfUiInput
                label={language.informacionPractica.trenLabel}
                disabled={calculatedTrainIsDisabled}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedTrain}
                maxLenght="6"
                placeholder={language.informacionPractica.trenLabel}
                value={selectedTrain}
              />
            </div>
          </div>
          <div className="rf-box__child">
            <div className="rf-practice-create__input-container">
              <RfUiInput
                label={language.informacionPractica.turnoLabel}
                error=""
                className="rf-input"
                onValueChanges={handleChangeSelectedTurn}
                maxLenght="10"
                placeholder={language.informacionPractica.turnoLabel}
                value={selectedTurn}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default PracticeInformation;
